import {
  AbstractControl,
  AbstractControlOptions,
  AsyncValidatorFn,
  FormArray,
  FormControl,
  FormControlOptions,
  FormControlState,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';
import * as _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { ValidatorsPlus } from '../../common/public-api';
import {
  FORM_VALIDATION_ERROR_MSG_MAP,
  FormControlEx,
  FormGroupEx,
  MessageItem,
  isNullOrEmpty,
} from '../../core/public-api';
import {
  convertToFormArray,
  setValueToFormControlArr,
} from '../../rt-shared/utils/form-utils';

export declare type ɵRawValue<T extends AbstractControl> =
  T extends AbstractControl<any, any>
    ? T['setValue'] extends (v: infer R) => void
      ? R
      : never
    : never;
/**
 * FormGroupEnhanced
 *
 * @export
 * @class FormGroupEnhanced
 * @extends {FormGroup}
 */
export class FormGroupEnhanced<
  TControl extends {
    [K in keyof TControl]:
      | EnhancedFormControl<any>
      | FormGroupEnhanced<any>
      | FormArrayEnhanced<any>;
  } = any
> extends FormGroup<TControl> {
  validationMsgMap = FORM_VALIDATION_ERROR_MSG_MAP;
  private _submitted = false;
  private _groupName = '';

  /**
   *群組名稱 getter
   */
  get groupName(): string {
    return this._groupName;
  }
  /**
   * 群組名稱 getter
   */
  set groupName(value: string) {
    this._groupName = value;
  }

  /**
   * 表單是否送出 setter
   */
  set submitted(value: boolean) {
    this.setSubmitValue(this, value);
  }

  constructor(
    controls: TControl,
    group?: string,
    validatorOrOpts?:
      | ValidatorFn
      | ValidatorFn[]
      | AbstractControlOptions
      | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
  ) {
    super(controls, validatorOrOpts, asyncValidator);
    this.groupName = group ?? '';
  }

  /**
   * 重置 FormGroup 狀態
   * override FormGroup reset
   *
   * @param {*} [value={}]
   * @param {{ onlySelf?: boolean, emitEvent?: boolean }} [options={}]
   * @memberof FormGroupEx
   */
  reset(
    value: any = {},
    options: { onlySelf?: boolean; emitEvent?: boolean } = {}
  ): void {
    this.submitted = false;
    super.reset(value, options);
  }
  /**
   * 取得向下所有controls
   * @returns
   */
  getControls(): EnhancedFormControl {
    return this.controls as EnhancedFormControl;
  }
  /**
   * 取得特定control
   * @returns
   */
  getControl(key: string): EnhancedFormControl {
    return this.get(key) as EnhancedFormControl;
  }
  /**
   * 取得FormGroup值並將control內的formArray更新回value
   * @returns
   */
  getRawValueWithSync(rawResp?: any) {
    for (let key in this.controls) {
      if (this.controls[key] instanceof EnhancedFormControl) {
        // console.log('getRawValueWithSync-group');
        const useControl = this.controls[key] as EnhancedFormControl;
        if (useControl.formArray) {
          useControl.formArray.controls.forEach((innerGroup: any) => {
            for (let innerKey in innerGroup.controls) {
              const useInnerControl = innerGroup.controls[innerKey];
              if (useInnerControl.formArray) {
                useInnerControl.setFormArrayToFormControl();
              }
            }
          });
          useControl.setFormArrayToFormControl();
        }
      }
    }
    let resp = this.getRawValue();
    if (rawResp) {
      resp = _.mergeWith(rawResp, resp, (a, b) =>
        isNullOrEmpty(a) ? b : undefined
      );
    }
    return resp;
  }
  /**
   * 設定FormGroup值並將control內的value更新回FormArray
   * @returns
   */
  async setValueWithSync(value: any) {
    const useValue = _.cloneDeep(value);
    if (useValue) {
      this.patchValue(useValue);
    }
    const syncFactory = (useControls: any[]) => {
      // console.log('syncFactory', value, useControls);
      useControls.forEach(async (innerGroup: any) => {
        // console.log('syncFactory-innerGroup', innerGroup);
        for (let innerKey in innerGroup.controls) {
          const useInnerControl = innerGroup.controls[innerKey];
          // console.log(
          //   'syncFactory-loop',
          //   innerKey,
          //   useInnerControl.getRawValue()
          // );
          if (
            useInnerControl.formArray ||
            useInnerControl.getRawValue() instanceof Array
          ) {
            if (!useInnerControl.formArray) {
              useInnerControl.formArray = new FormArrayEnhanced(
                [] as FormGroupEnhanced<IFormConfig<any>>[]
              );
            }
            await useInnerControl.setFormControlToFormArray();
            // console.log('syncFactory-over', useInnerControl);
            if (useInnerControl.formArray) {
              // console.log(
              //   'syncFactory-inner',
              //   useInnerControl,
              //   useInnerControl.formArray
              // );
              syncFactory(useInnerControl.formArray.controls);
            }
          }
        }
      });
    };
    for (let key in this.controls) {
      if (this.controls[key] instanceof EnhancedFormControl) {
        // console.log('setValueWithSync-group');
        const useControl = this.controls[key] as EnhancedFormControl;
        if (useControl.formArray || useControl.getRawValue() instanceof Array) {
          if (!useControl.formArray) {
            useControl.formArray = new FormArrayEnhanced(
              [] as FormGroupEnhanced<IFormConfig<any>>[]
            );
          }
          await useControl.setFormControlToFormArray();
          syncFactory(useControl.formArray.controls);
        }
      }
    }
  }

  /**
   * 檢核 FormGroup
   *
   * @memberof FormGroupEnhanced
   */
  validateAll(): void {
    for (let key in this.controls) {
      if (this.controls[key] instanceof EnhancedFormControl) {
        const useControl = this.controls[key] as EnhancedFormControl;
        if (useControl.formArray) {
          useControl.formArray.controls.forEach((innerGroup: any) => {
            for (let innerKey in innerGroup.controls) {
              const useInnerControl = innerGroup.controls[innerKey];
              if (useInnerControl.formArray) {
                useInnerControl.setFormArrayToFormControl();
              }
            }
            innerGroup.validateAll();
          });
        }
      }
    }
    this.validateAllControls(this);
    this.markAllAsTouched();
  }
  /**
   * 檢核 FormGroup僅格式
   *
   * @memberof FormGroupEnhanced
   */
  validateAllFormat(): void {
    this.validateAllControlsFormat(this);
    this.markAllAsTouched();
  }

  /**
   * 檢核 FormGroup僅格式
   *
   * @memberof FormGroupEnhanced
   */
  validateAllLength(): void {
    this.validateAllControlsLength(this);
    this.markAllAsTouched();
  }
  /**
   * 取得控制項檢核錯誤訊息
   *
   * @param {(AbstractControl | null)} control
   * @param {string} [controlName]
   * @return {*}  {MessageItem[]}
   * @memberof FormGroupEx
   */
  getFormValidationErrorMessages(
    control: AbstractControl | null,
    controlName?: string
  ): MessageItem[] {
    if (isNullOrEmpty(control)) {
      return [];
    }
    const checkDisplayName = (
      useName: string | undefined,
      controlName: string | undefined
    ) => {
      if (isNullOrEmpty(useName)) {
        return controlName && controlName.replace(/[A-Z]/g, (m) => ' ' + m);
      } else {
        return useName;
      }
    };
    if (control instanceof FormControl) {
      const errKeys = Object.keys(control?.errors ?? {});
      const category =
        control instanceof FormControlEx ||
        control instanceof EnhancedFormControl
          ? checkDisplayName(control.displayName, controlName)
          : controlName;
      const msgItems = errKeys.map((errName) => {
        let msg =
          this.validationMsgMap[
            errName as keyof typeof this.validationMsgMap
          ] ?? errName;
        if (errName === 'maxlength') {
          msg = msg.replace(
            '{actualLength}',
            String(control?.errors?.['maxlength']?.['actualLength'])
          );
          msg = msg.replace(
            '{requiredLength}',
            String(control?.errors?.['maxlength']?.['requiredLength'])
          );
        } else if (errName === 'minlength') {
          msg = msg.replace(
            '{currentLength}',
            String(control?.errors?.['minlength']?.['actualLength'])
          );
          msg = msg.replace(
            '{minimumLength}',
            String(control?.errors?.['minlength']?.['requiredLength'])
          );
        } else if (errName === 'maxDateComp') {
          msg = msg.replace(
            '{maxDateComp}',
            String(control?.errors?.['maxDateComp']?.['maxDate'])
          );
        } else if (errName === 'customErrorMessage') {
          msg = String(control?.errors?.['customErrorMessage']);
        } else if (
          errName === 'minDate' &&
          control?.errors?.['minDate']?.['custMsg']
        ) {
          msg = String(control?.errors?.['minDate']?.['custMsg']);
        }
        return { category: category, message: msg } as MessageItem;
      });

      // formControl 底下有 formArray 時進行以下處理
      const formArrayControl = control as any;
      if (msgItems.length == 0 && !isNullOrEmpty(formArrayControl.formArray)) {
        let errMsgItems: MessageItem[] = [];
        const array = formArrayControl.formArray as FormArray;
        for (let i = 0; i < array.length; i++) {
          const ctrl = array.at(i);
          errMsgItems = errMsgItems.concat(
            this.getFormValidationErrorMessages(ctrl, i.toString())
          );
        }
        return errMsgItems;
      }
      return msgItems;
    }

    let errMsgItems: MessageItem[] = [];
    if (control instanceof FormGroup) {
      const form = control as FormGroup;
      Object.keys(form.controls).forEach((ctrlName, idx) => {
        const ctrl = form.get(ctrlName);
        errMsgItems = errMsgItems.concat(
          this.getFormValidationErrorMessages(ctrl, ctrlName)
        );
      });
    }

    if (control instanceof FormArray) {
      const array = control as FormArray;
      for (let i = 0; i < array.length; i++) {
        const ctrl = array.at(i);
        errMsgItems = errMsgItems.concat(
          this.getFormValidationErrorMessages(ctrl, i.toString())
        );
      }
    }

    return errMsgItems;
  }

  /**
   * 遞迴檢核 FormGroupEx 下所有控制項
   *
   * @private
   * @param {(AbstractControl | null)} target
   * @return {*}  {void}
   * @memberof FormGroupEx
   */
  private validateAllControls(target: AbstractControl | null): void {
    if (isNullOrEmpty(target)) {
      return;
    }
    if (
      target instanceof FormControl ||
      target instanceof EnhancedFormControl
    ) {
      target.updateValueAndValidity();

      return;
    }
    if (target instanceof FormGroup) {
      const group = target as FormGroup;
      Object.keys(group.controls).forEach((ctrlName) => {
        const ctrl = group.get(ctrlName);
        this.validateAllControls(ctrl);
      });
      return;
    }
    if (target instanceof FormArray) {
      const array = target as FormArray;
      array.controls.forEach((ctrl) => {
        this.validateAllControls(ctrl);
      });
      return;
    }
  }

  /**
   * 遞迴檢核 FormGroupEx 下所有控制項
   *
   * @private
   * @param {(AbstractControl | null)} target
   * @return {*}  {void}
   * @memberof FormGroupEx
   */
  private async validateAllControlsFormat(
    target: AbstractControl | null
  ): Promise<void> {
    if (isNullOrEmpty(target)) {
      return;
    }
    if (
      target instanceof FormControl ||
      target instanceof EnhancedFormControl
    ) {
      if (target.hasValidator(ValidatorsPlus.required)) {
        target.removeValidators(ValidatorsPlus.required);

        await target.updateValueAndValidity();
        target.addValidators(ValidatorsPlus.required);
        return;
      }
    }

    if (target instanceof FormGroup) {
      const group = target as FormGroup;
      Object.keys(group.controls).forEach((ctrlName) => {
        const ctrl = group.get(ctrlName);
        this.validateAllControlsFormat(ctrl);
      });
      return;
    }
    if (target instanceof FormArray) {
      const array = target as FormArray;
      array.controls.forEach((ctrl) => {
        this.validateAllControlsFormat(ctrl);
      });
      return;
    }
  }
  /**
   * 遞迴檢核 FormGroupEx 下所有控制項
   *
   * @private
   * @param {(AbstractControl | null)} target
   * @return {*}  {void}
   * @memberof FormGroupEx
   */
  private async validateAllControlsLength(
    target: AbstractControl | null
  ): Promise<void> {
    if (isNullOrEmpty(target)) {
      return;
    }
    if (target instanceof EnhancedFormControl) {
      const useControl = target as EnhancedFormControl;
      if (useControl.formArray) {
        useControl.formArray.controls.forEach((innerGroup: any) => {
          for (let innerKey in innerGroup.controls) {
            const useInnerControl = innerGroup.controls[
              innerKey
            ] as EnhancedFormControl;
            if (useInnerControl.formArray) {
              useInnerControl.setFormArrayToFormControl();
              useInnerControl.clearValidators();
              useInnerControl.addValidators(
                ValidatorsPlus.maxLength(useInnerControl.length ?? 999999)
              );
            }
          }
          innerGroup.validateAllControlsLength(innerGroup);
        });
      } else {
        let targetFormControl = target as any;
        let rawValidators = targetFormControl._rawValidators;
        if (!isNullOrEmpty(rawValidators)) {
          await target.clearValidators();
        }
        if (!isNullOrEmpty(target.length)) {
          target.addValidators(
            ValidatorsPlus.maxLength(target.length ?? 999999)
          );
        }
        if (!isNullOrEmpty(rawValidators) || !isNullOrEmpty(target.length)) {
          await target.updateValueAndValidity();
        }
        return;
      }
    }

    if (target instanceof FormGroup || target instanceof FormGroupEnhanced) {
      const group = target as FormGroup;
      Object.keys(group.controls).forEach((ctrlName) => {
        const ctrl = group.get(ctrlName);
        this.validateAllControlsLength(ctrl);
      });
      return;
    }
    if (target instanceof FormArray || target instanceof FormArrayEnhanced) {
      const array = target as FormArray;
      array.controls.forEach((ctrl) => {
        this.validateAllControlsLength(ctrl);
      });
      return;
    }
  }

  /**
   * 遞迴設定表單送出狀態
   *
   * @private
   * @param {(AbstractControl | null)} target
   * @param {boolean} value
   * @return {*}  {void}
   * @memberof FormGroupEx
   */
  private setSubmitValue(target: AbstractControl | null, value: boolean): void {
    if (isNullOrEmpty(target)) {
      return;
    }
    if (target instanceof FormGroupEx || target instanceof FormGroupEnhanced) {
      if (target === this) {
        this._submitted = value;
      } else {
        target.submitted = value;
      }
    }
    if (target instanceof FormControl) {
      return;
    }
    if (target instanceof FormGroup) {
      const group = target as FormGroup;
      Object.keys(group.controls).forEach((ctrlName) => {
        const ctrl = group.get(ctrlName);
        this.setSubmitValue(ctrl, value);
      });
      return;
    }
    if (target instanceof FormArray) {
      const array = target as FormArray;
      array.controls.forEach((ctrl) => {
        this.setSubmitValue(ctrl, value);
      });
      return;
    }
  }
  genFormByConfig<T>(
    config: IConfig<T>,
    form?:
      | AbstractControl
      | FormGroupEnhanced
      | FormGroupEx
      | FormControl
      | EnhancedFormControl
      | undefined,
    rightNowConfig?: IEnhancedAbstractControlOptions
  ): void {
    const processConfig = (
      form: EnhancedFormControl,
      config: IEnhancedAbstractControlOptions
    ) => {
      // console.log('processConfig', form, config);
      if (config?.isRequired) {
        if (!form.hasValidator(ValidatorsPlus.required))
          form.addValidators(ValidatorsPlus.required);
      }
      if (config.displayName) {
        form.displayName = config.displayName;
      }
      if (config?.length && config.length > 0) {
        form.length = config.length;
        form.addValidators(ValidatorsPlus.maxLength(config.length));
        // console.log(
        //   'genFormByConfig-length',
        //   form.hasValidator(ValidatorsPlus.maxLength(config.length))
        // );
      }
      if (config?.customerValidate) {
        config.customerValidate.forEach((validator) => {
          if (!form.hasValidator(validator)) {
            form.addValidators(validator);
          }
        });
      }
    };
    // console.log('genFormByConfig', rightNowConfig, form, this);
    if (form && rightNowConfig && form instanceof EnhancedFormControl) {
      processConfig(form, rightNowConfig);
    }
    // console.log('genFormByConfig-config', config, form);
    for (const key of Object.keys(config)) {
      const useKey = key as Extract<keyof T, string>;
      const useControl = this.controls as any;
      let target = useControl?.[key];
      // console.log(
      //   'genFormByConfig-loop',
      //   key,
      //   target instanceof FormGroupEnhanced,
      //   target instanceof EnhancedFormControl,
      //   target instanceof FormArrayEnhanced,
      //   useControl,
      //   target,
      //   isNullOrEmpty(target)
      // );

      if (isNullOrEmpty(target)) {
      }
      if (
        target instanceof FormGroupEx ||
        target instanceof FormGroupEnhanced
      ) {
        const group = target;
        // console.log('genFormByConfig-group', target, useKey, config[useKey]);
        const useConfig = config[useKey] as any;
        if (Object.keys(group.controls)?.length === 0) {
          Object.keys(config[useKey]).forEach((key) => {
            group.addControl(key, new EnhancedFormControl(useConfig[key]));
          });
        }
        Object.keys(group.controls).forEach((ctrlName) => {
          const ctrl = group.get(ctrlName) as EnhancedFormControl;

          // console.log(
          //   'genFormByConfig-group-foreach',
          //   'ctrl:',
          //   ctrl,
          //   ' ctrlName:',
          //   ctrlName,
          //   ' useKey:',
          //   useKey,
          //   ' config[useKey]',
          //   config[useKey]
          // );
          if (ctrl) {
            if (config[useKey]?.displayName)
              this.genFormByConfig<T>(
                config,
                ctrl,
                config[useKey] as IEnhancedAbstractControlOptions
              );
            else {
              const useConfig = config[useKey] as any;
              if (useConfig[ctrlName])
                this.genFormByConfig<T>(
                  useConfig[ctrlName],
                  ctrl,
                  useConfig[ctrlName]
                );
            }
          }
        });
      } else if (target instanceof FormArrayEnhanced) {
        /**
         * FormArray暫時不以genform方式實作
         */
        const array = target as FormArrayEnhanced;
        // console.log(
        //   'genFormByConfig-array',
        //   target,
        //   array.controls[0],
        //   array.controls.length
        // );
        for (let index = 0; index < array.controls.length; index++) {
          // console.log('genFormByConfig-array-for', config, index);
          if (array.controls[index]) {
            // console.log('genFormByConfig-array-ctrl', array.controls[index]);
            const useFormConfig = array.controls[index] as FormGroupEnhanced;
            Object.keys(useFormConfig.controls).forEach((ctrlName) => {
              // console.log(
              //   'genFormByConfig-array-ctrl-foreach',
              //   ctrlName,
              //   config[useKey]
              // );
              const useConfig = config[useKey] as any;
              if (useConfig[ctrlName]) {
                this.genFormByConfig<T>(
                  useConfig[ctrlName],
                  useFormConfig.controls[ctrlName],
                  useConfig[ctrlName]
                );
              }
            });
          }
        }
      } else if (target instanceof EnhancedFormControl && target?.formArray) {
        const array = target.formArray as FormArrayEnhanced;
        const useConfig = _.cloneDeep(config);
        target.formArrayConfig = useConfig[useKey] as any;
        // console.log(
        //   'genFormByConfig-innerarray',
        //   target,
        //   array.controls[0],
        //   array.controls.length
        // );
        for (let index = 0; index < array.controls.length; index++) {
          // console.log('genFormByConfig-innerarray-for', config, index);
          if (array.controls[index]) {
            // console.log(
            //   'genFormByConfig-innerarray-ctrl',
            //   array.controls[index]
            // );
            const useFormConfig = array.controls[index] as FormGroupEnhanced;
            Object.keys(useFormConfig.controls).forEach((ctrlName) => {
              // console.log(
              //   'genFormByConfig-innerarray-ctrl-foreach',
              //   ctrlName,
              //   config[useKey]
              // );
              const useConfig = config[useKey] as any;
              if (useConfig[ctrlName]) {
                this.genFormByConfig<T>(
                  useConfig[ctrlName],
                  useFormConfig.controls[ctrlName],
                  useConfig[ctrlName]
                );
              }
            });
          }
        }
      } else {
        if (!(target instanceof EnhancedFormControl)) {
          // console.log('genFormByConfig-null', key, config);
          const useConfig = _.cloneDeep(config);
          useControl[key] = new EnhancedFormControl(useConfig[useKey]);
          target = useControl[key];
        }
        // console.log('genFormByConfig-addValida', target);
        processConfig(
          target,
          config[useKey] as IEnhancedAbstractControlOptions
        );
      }
    }
  }
}
/**
 *  form config改寫，因目前angular原生option的type問題，造成繼承FormConfig後無法傳入泛型
 * 因此在改寫上需要重新定義每個介面,型別
 */

/**
 * form config 客制變數
 */
export interface IEnhancedAbstractControlOptions {
  // custom attribute
  displayName?: string;
  length?: number;
  isRequired?: boolean;
  customerValidate?: ValidatorFn[];
  readonly: boolean;
  hidden: boolean;
  type?: PropertyType;
  isFormArray?: boolean;
}
export enum PropertyType {
  NUMBER = 'number',
  STRING = 'string',
  DATE = 'date',
}

/**
 * form config 客制變數預設值
 */
const defaultEnhancedAbstractControlOptions: IEnhancedAbstractControlOptions = {
  displayName: '',
  length: 999999,
  readonly: false,
  hidden: false,
};
/**
 * EnhancedFormControl formConfig改寫 介面
 */
export interface EnhancedFormControl<TValue = any> extends FormControl<TValue> {
  formArrayConfig: IConfig<any>;
  formConfig?: string | Partial<IEnhancedAbstractControlOptions>;
  displayName?: string;
  readonly: boolean;
  readonlyChanges: Observable<boolean>;
  hidden: boolean;
  hiddenChanges: Observable<boolean>;
  warnings: EnhancedFormControl['errors'];
  setArrayValue: Function;
  getFormControlArrayRawValue: Function;
  setFormArrayToFormControl: Function;
  setFormControlToFormArray: Function;
  formArray?: FormArrayEnhanced;
  addArrayRow: Function;
  delecteArrayRow: Function;
  formOriginValue: any;
  setReadOnly: Function;
  length?: number;
}
/**
 * EnhancedFormControl formConfig改寫 型別
 */
type EnhancedFormControlInterface<TValue = any> = EnhancedFormControl<TValue>;

// Internal class, rip-off ɵFormControlCtor with enhanced typings
interface ɵEnhancedFormControlCtor {
  new (): EnhancedFormControl<any>;
  new <T = any>(
    formConfig?: string | Partial<IEnhancedAbstractControlOptions>
  ): EnhancedFormControl<T>;
  new <T = any>(displayName: string): EnhancedFormControl<T>;
  new <T = any>(
    value: FormControlState<T> | T,
    opts: FormControlOptions & { nonNullable: true }
  ): EnhancedFormControl<T>;
  new <T = any>(
    displayName: string,
    value: FormControlState<T> | T,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
    _opts?: Partial<IEnhancedAbstractControlOptions>
  ): EnhancedFormControl<T>;

  prototype: EnhancedFormControl<any>;
}

export const EnhancedFormControl: ɵEnhancedFormControlCtor = class EnhancedFormControl<
    TValue = any
  >
  extends FormControl
  implements EnhancedFormControlInterface<TValue>
{
  displayName?: string;
  length?: number;
  isRequired?: boolean;
  custormValidateMsg?: Record<string, string>[] = [];
  formArray = null as any;
  formArrayConfig = {} as IConfig<any>;
  formOriginValue = null as any;
  private _readonly: boolean;
  private _readonlyChanges = new Subject<boolean>();
  set displayname(setName: string | undefined) {
    this.displayName = setName;
  }

  get readonly() {
    return this._readonly;
  }
  set readonly(readonly: boolean) {
    this._readonly = readonly;
    this._readonlyChanges.next(readonly);
  }
  get readonlyChanges(): Observable<boolean> {
    return this._readonlyChanges;
  }
  private _hidden: boolean;
  private _hiddenChanges = new Subject<boolean>();
  get hidden() {
    return this._hidden;
  }
  set hidden(hidden: boolean) {
    this._hidden = hidden;
    this._hiddenChanges.next(hidden);
  }
  get hiddenChanges(): Observable<boolean> {
    return this._hiddenChanges;
  }
  warnings: EnhancedFormControl['errors'] = null;
  setReadOnly(readonly: boolean) {
    this._readonly = readonly;
    this._readonlyChanges.next(readonly);
  }
  /**
   * 設定control值至FormArray
   * @returns
   */
  setFormControlToFormArray() {
    if (!this.formArray) return;
    const useControl = this.getRawValue() as any;
    convertToFormArray(this.formArray, useControl, this.formArrayConfig);
  }
  /**
   * 設定 FormArray值至control
   * @returns
   */
  setFormArrayToFormControl() {
    if (!this.formArray) return;
    setValueToFormControlArr(this, this.formArray);
  }

  setCustormValidateMsg(msg: Record<string, string>) {
    this.custormValidateMsg?.push(msg);
  }

  getCustormValidateMsg(key: string) {
    let returnMsg = null;
    this.custormValidateMsg?.forEach((msg) => {
      if (msg[key]) {
        returnMsg = msg[key];
      }
    });
    return returnMsg;
  }
  /**
   * 對formArray新增一row
   */
  addArrayRow() {
    const useFormGroup = new FormGroupEnhanced({});
    useFormGroup.genFormByConfig(this.formArrayConfig);
    this.formArray.controls.push(useFormGroup);
  }
  /**
   * 刪除formArray指定元素
   */
  delecteArrayRow(index: number) {
    this.formArray.controls.remove(index);
  }
  /**
   * 對formControl設定value並同步更新回formArray
   * @param value
   */
  async setArrayValue(value: any) {
    if (!this.formArray) {
      this.formArray = new FormArrayEnhanced(
        [] as FormGroupEnhanced<IFormConfig<TValue>>[]
      );
    }
    await this.setValue(value);
    await this.setFormControlToFormArray();
    this.formArray.setFormArrayWithSync();
  }
  /**
   * 取得formControl的值並同步將formArray的值更新至control
   * @returns
   */
  getFormControlArrayRawValue() {
    this.setFormArrayToFormControl();
    return this.getRawValue();
  }
  constructor(
    formConfig?: string | Partial<IEnhancedAbstractControlOptions>,
    // formState and defaultValue will only be null if T is nullable
    formState: FormControlState<TValue> | TValue = null as unknown as TValue,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null,
    _opts?: Partial<IEnhancedAbstractControlOptions>
  ) {
    super(formState, validatorOrOpts, asyncValidator);
    const { readonly, hidden } = {
      ...defaultEnhancedAbstractControlOptions,
      ..._opts,
    };
    this._readonly = readonly;
    this._hidden = hidden;
    if (typeof formConfig === 'string') {
      this.displayName = formConfig as string;
    } else if (_.isObjectLike(formConfig)) {
      this.displayName = formConfig?.displayName;
      if (formConfig?.isFormArray) {
        // console.log('isFormArray-config', formConfig);
        this.formArray = new FormArrayEnhanced(
          [] as FormGroupEnhanced<IFormConfig<TValue>>[]
        );
        const useConfig = _.cloneDeep(formConfig);
        delete useConfig['isFormArray'];
        this.formArrayConfig = useConfig as IConfig<any>;
      }
    }
  }
};

export class FormArrayEnhanced<
  TControl extends EnhancedFormControl | AbstractControl<any, any> = any
> extends FormArray<TControl> {
  private _groupName = '';

  /**
   *群組名稱 getter
   */
  get groupName(): string {
    return this._groupName;
  }
  /**
   * 群組名稱 getter
   */
  set groupName(value: string) {
    this._groupName = value;
  }
  constructor(
    controls: Array<TControl>,
    group?: string,
    validatorOrOpts?:
      | ValidatorFn
      | ValidatorFn[]
      | AbstractControlOptions
      | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
  ) {
    super(controls, validatorOrOpts, asyncValidator);
    this.groupName = group ?? '';
  }
  getArrayControls(): FormGroupEnhanced[] {
    return this.controls as any;
  }
  pushFormGroup(formGroup: TControl) {
    this.push(formGroup);
  }
  validateFormArray() {
    this.controls.forEach((group: any) => {
      group.validateAll();
    });
  }
  validateFormArrayFormat() {
    this.controls.forEach((group: any) => {
      group.validateAllFormat();
    });
  }
  validateFormArrayLength() {
    this.controls.forEach((group: any) => {
      group.validateAllLength();
    });
  }
  getFormArrayValidationErrorMessages(): MessageItem[] {
    let formArrayErrorMsgList = [] as MessageItem[];
    this.controls.forEach((group: any) => {
      formArrayErrorMsgList = formArrayErrorMsgList.concat(
        group.getFormValidationErrorMessages(group)
      );
    });
    return formArrayErrorMsgList;
  }
  getFormArrayRawValue(): any[] {
    let useArray = [] as any[];
    this.controls.forEach((group: any) => {
      const useGroup = group as FormGroupEnhanced;
      useArray.push(useGroup.getRawValueWithSync());
    });
    return useArray;
  }
  async setFormArrayWithSync() {
    for (let key in this.controls) {
      if (this.controls[key] instanceof FormGroupEnhanced) {
        // console.log('setValueWithSync-group');
        const useFormGroup = this.controls[key] as FormGroupEnhanced;
        await useFormGroup.setValueWithSync(null);
      }
    }
    this.setValue(this.getFormArrayRawValue());
  }
}
export type IFormConfig<T> = {
  [K in keyof T]: EnhancedFormControl<T[K]>;
};
type anyArray = {
  [K in any]: any[] | Array<any>;
};
type anyType = {
  [K in any]: any;
};
type ArrElement<ArrType> = ArrType extends readonly (infer ElementType)[]
  ? ElementType
  : never;
export type IForm<T> = {
  [K in keyof T]: T[K] extends
    | string
    | number
    | Date
    | boolean
    | undefined
    | (infer anyArray)[]
    ? EnhancedFormControl<T[K]>
    : FormGroupEnhanced<IForm<T[K]>>;
};
export type IConfig<T> = {
  [K in keyof T]:
    | IEnhancedAbstractControlOptions
    | Record<string, IEnhancedAbstractControlOptions>;
};
export type IFormCluster<T> = {
  [K in keyof T]: T[K] extends (infer anyType)[] | Array<anyType>
    ? FormArrayEnhanced<FormGroupEnhanced<IFormConfig<ArrElement<T[K]>>>>
    : FormGroupEnhanced<IForm<T[K]>>;
};
export class FormCluster<T> {
  controls!: IFormCluster<T>;
  rawValue: any;
  setClusterValue(obj: any) {
    this.rawValue = obj;
    for (let key in this.controls) {
      try {
        if (this.controls[key] instanceof FormGroupEnhanced) {
          const useGroup = this.controls[key] as FormGroupEnhanced;
          useGroup.setValueWithSync(obj?.[key]);
        } else if (this.controls[key] instanceof FormArrayEnhanced) {
          // console.log('setValue-array', key);
          const useArr = this.controls[key] as FormArrayEnhanced;
          convertToFormArray(useArr, obj?.[key]);
        }
      } catch (error) {}
    }
  }
  validateCluster() {
    for (let key in this.controls) {
      if (this.controls[key] instanceof FormGroupEnhanced) {
        // console.log('validateCluster-group');
        const useGroup = this.controls[key] as FormGroupEnhanced;
        useGroup.validateAll();
      } else if (this.controls[key] instanceof FormArrayEnhanced) {
        // console.log('validateCluster-array');
        const useArr = this.controls[key] as FormArrayEnhanced;
        useArr.validateFormArray();
      }
    }
  }
  validateClusterFormat() {
    for (let key in this.controls) {
      if (this.controls[key] instanceof FormGroupEnhanced) {
        // console.log('validateCluster-group');
        const useGroup = this.controls[key] as FormGroupEnhanced;
        useGroup.validateAllFormat();
      } else if (this.controls[key] instanceof FormArrayEnhanced) {
        // console.log('validateCluster-array');
        const useArr = this.controls[key] as FormArrayEnhanced;
        useArr.validateFormArrayFormat();
      }
    }
  }
  validateClusterLength() {
    for (let key in this.controls) {
      if (this.controls[key] instanceof FormGroupEnhanced) {
        // console.log('validateCluster-group');
        const useGroup = this.controls[key] as FormGroupEnhanced;
        useGroup.validateAllLength();
      } else if (this.controls[key] instanceof FormArrayEnhanced) {
        // console.log('validateCluster-array');
        const useArr = this.controls[key] as FormArrayEnhanced;
        useArr.validateFormArrayLength();
      }
    }
  }
  getFormClusterValidationErrorMessages(): ClusterErrorMsg[] {
    let formArrayErrorMsgList = [] as ClusterErrorMsg[];
    for (let key in this.controls) {
      if (this.controls[key] instanceof FormGroupEnhanced) {
        // console.log('validateCluster-group');
        const useGroup = this.controls[key] as FormGroupEnhanced;
        const useClusterErrorMsg = {} as ClusterErrorMsg;
        useClusterErrorMsg.groupName = useGroup.groupName;
        useClusterErrorMsg.msgList =
          useGroup.getFormValidationErrorMessages(useGroup);
        formArrayErrorMsgList.push(useClusterErrorMsg);
      } else if (this.controls[key] instanceof FormArrayEnhanced) {
        let useArrayErrorMsgList = [] as MessageItem[];
        // console.log('validateCluster-array');
        const useArr = this.controls[key] as FormArrayEnhanced;
        let useGroupName = '';
        useArr.controls.forEach((group: any) => {
          const useGroup = group as FormGroupEnhanced;
          useGroupName = useArr.groupName;
          useArrayErrorMsgList = useArrayErrorMsgList.concat(
            useGroup.getFormValidationErrorMessages(useGroup)
          );
        });
        const useClusterErrorMsg = {} as ClusterErrorMsg;
        useClusterErrorMsg.groupName = useGroupName;
        useClusterErrorMsg.msgList = useArrayErrorMsgList;
        formArrayErrorMsgList.push(useClusterErrorMsg);
      }
    }

    return formArrayErrorMsgList;
  }
  /**
   * rawResp 原始api回傳值
   */
  getFormClusterRawValue(rawResp?: T): T {
    const useCluster = {} as T;
    for (let key in this.controls) {
      if (this.controls[key] instanceof FormGroupEnhanced) {
        // console.log('validateCluster-group');
        const useGroup = this.controls[key] as FormGroupEnhanced;
        useCluster[key as unknown as keyof T] =
          useGroup?.getRawValueWithSync(rawResp ? rawResp?.[key] : undefined) ??
          null;
      } else if (this.controls[key] instanceof FormArrayEnhanced) {
        // console.log('validateCluster-array');
        const useArr = this.controls[key] as FormArrayEnhanced;
        useCluster[key as unknown as keyof T] =
          (useArr?.getFormArrayRawValue() as any) ?? null;
      }
    }
    return useCluster;
  }

  constructor(formCluster: IFormCluster<T>) {
    this.controls = formCluster;
  }
}
interface ClusterErrorMsg {
  groupName: string;
  msgList: MessageItem[];
}
